import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppointmentBooking, TimeSlot } from '../../../hooks/useAppointmentBooking';
import previousIcon from '../../../assets/icons/previous-circled-carret-icon.svg';
import nextIcon from '../../../assets/icons/next-circled-carret-icon.svg';
import { Loader } from '../../Loader/Loader';
import './DateTimeStep.scss';

interface DateTimeStepProps {
  onNext: (data: Record<string, any>) => void;
  setStepValidity?: (isValid: boolean) => void;
  initialData?: Record<string, any>;
  appointmentId?: string;
  showroom?: string;
}


export const DateTimeStep: React.FC<DateTimeStepProps> = ({
  onNext,
  setStepValidity,
  initialData = {},
  appointmentId = '',
  showroom = ''
}) => {

  const { t } = useTranslation('appointments');
  const { getAvailableSlots } = useAppointmentBooking();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(initialData.date || '');
  const [selectedTime, setSelectedTime] = useState(initialData.time || '');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  
  // Set validation state based on date and time selection
  useEffect(() => {
    if (setStepValidity) {
      // For DateTimeStep, we need to check if date and time are selected
      const isValid = !!(selectedDate && (selectedTimeSlot || selectedTime));
      setStepValidity(isValid);
    }
  }, [selectedDate, selectedTimeSlot, selectedTime, setStepValidity]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  
  // Fetch available slots when appointmentId or showroom changes
  const { data: availableSlotsData, isLoading, isError } = getAvailableSlots(appointmentId, showroom);
  
  // This useEffect was removed to avoid duplicate processAvailableSlotsData calls

  // Process data from API when it's available
  useEffect(() => {
    // Handle both direct array response and data property
    if (Array.isArray(availableSlotsData) && availableSlotsData.length > 0) {
      processAvailableSlotsData(availableSlotsData);
    } else if (availableSlotsData && availableSlotsData.data && Array.isArray(availableSlotsData.data) && availableSlotsData.data.length > 0) {
      processAvailableSlotsData(availableSlotsData);
    }
  }, [availableSlotsData, appointmentId, showroom]);

  // Process available slots data function
  const processAvailableSlotsData = (data: any) => {
    // Check if data is an array (direct response) or has a data property
    const slotsArray = Array.isArray(data) ? data : data?.data;
    
    if (slotsArray && slotsArray.length > 0) {
      // Filter out invalid slots
      const validSlots = slotsArray.filter((slot: TimeSlot) => {
        return slot && slot.startTime && slot.endTime;
      });
      
      if (validSlots.length === 0) {
        return;
      }
      
      // Extract unique dates from available slots
      const dateValues = validSlots.map((slot: TimeSlot) => {
        const date = new Date(slot.startTime);
        return formatDateToYYYYMMDD(date);
      });
      
      // Get unique dates
      const uniqueDates = Array.from(new Set(dateValues)) as string[];
      
      setAvailableDates(uniqueDates);
      
      // If we have a selected date, filter time slots for that date
      if (selectedDate) {
        filterTimeSlotsByDate(selectedDate);
      }
    }
  };
  
  // This useEffect has been removed as it was only used for debugging
  
  // Update time slots when selected date changes
  useEffect(() => {
    if (selectedDate && availableSlotsData?.data) {
      filterTimeSlotsByDate(selectedDate);
    }
  }, [selectedDate, availableSlotsData]);
  
  /**
   * Helper function to format dates consistently in UTC
   * This ensures that regardless of the user's local timezone,
   * we always compare dates in UTC format to match the API's +0000 format
   * 
   * @param date - Date object to format
   * @returns string in YYYY-MM-DD format using UTC
   */
  const formatDateToYYYYMMDD = (date: Date): string => {
    // Always use UTC methods to ensure consistency with API's +0000 format
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  };

  /**
   * Filter time slots by the selected date
   * This function handles different timezone formats from the API by:
   * 1. Converting each slot's startTime to a Date object (which normalizes timezone differences)
   * 2. Using formatDateToYYYYMMDD to get the date in UTC format
   * 3. Comparing with the selected date (which is also in UTC format)
   * 
   * @param date - Selected date in YYYY-MM-DD format
   */
  const filterTimeSlotsByDate = (date: string) => {
    // Check if data is available either as direct array or in data property
    const slotsArray = Array.isArray(availableSlotsData) 
      ? availableSlotsData 
      : availableSlotsData?.data;
      
    if (!slotsArray || slotsArray.length === 0) {
      return;
    }
    
    const filteredSlots = slotsArray.filter((slot: TimeSlot) => {
      // Create Date object from the slot's startTime (handles any timezone format: Z, +0000, etc.)
      const slotDate = new Date(slot.startTime);
      // Format to YYYY-MM-DD in UTC for consistent comparison
      const slotDateStr = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}-${String(slotDate.getDate()).padStart(2, '0')}`;
      // Compare with selected date
      return date === slotDateStr;
    });
    
    // Sort time slots by start time
    const sortedSlots = filteredSlots.sort((a: TimeSlot, b: TimeSlot) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    
    setTimeSlots(sortedSlots);
  };
  
  /**
   * Format time for display in the UI
   * Converts ISO time string to localized time format (e.g., "10:00 AM")
   * 
   * @param timeString - ISO 8601 time string
   * @returns Formatted time string for display
   */
  const formatTime = (timeString: string): string => {
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };
  
  /**
   * Format time to match the API's expected format with Z timezone suffix
   * This is used when submitting the form to ensure the backend receives
   * dates in the format it expects (ISO 8601 with Z suffix)
   * 
   * @param timeString - ISO 8601 time string
   * @returns Formatted time string with Z timezone suffix (2025-08-31T15:30:00.000Z)
   */
  const formatGMTTime = (timeString: string): string => {
    const date = new Date(timeString);
    // Use standard ISO format with Z suffix as required by the backend
    return date.toISOString();
  };

  /**
   * Validate and submit the form when a date and time slot are selected
   * Performs validation to ensure both date and time are selected before proceeding
   * 
   * @param e - Form submission event
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that both date and time are selected
    if (!selectedDate) {
      // Date validation failed
      return;
    }
    
    if (!selectedTime && !selectedTimeSlot) {
      // Time validation failed
      return;
    }
    
    // If we have a selected time slot from the API, use its data
    if (selectedTimeSlot) {
      // Check if we have the workTypeGroupId in the selected time slot
      // This should be a Salesforce ID starting with "0VSD"
      const workTypeGroupId = (selectedTimeSlot as any).workTypeGroupId;
      
      if (!workTypeGroupId || typeof workTypeGroupId !== 'string' || !workTypeGroupId.startsWith('0VSD')) {
        console.error('Invalid or missing workTypeGroupId in selected time slot:', selectedTimeSlot);
      }
      
      onNext({
        date: selectedDate,
        time: `${formatTime(selectedTimeSlot.startTime)} - ${formatTime(selectedTimeSlot.endTime)}`,
        startTime: formatGMTTime(selectedTimeSlot.startTime),
        endTime: formatGMTTime(selectedTimeSlot.endTime),
        serviceTerritoryId: selectedTimeSlot.territoryId, // Match backend field name
        resourceIds: [selectedTimeSlot.resourceId], // Convert to array as required by backend
        resourceName: selectedTimeSlot.resourceName,
        workTypeGroupId: workTypeGroupId // Include the workTypeGroupId from the API response
      });
    } else {
      // Fallback to simple date/time for compatibility with original code
      onNext({
        date: selectedDate,
        time: selectedTime
      });
    }
  };

  return (
    <div className="date-time-step">
      {isLoading ? (
        <Loader variant="overlay" />
      ) : isError ? (
        <div className="date-time-step__error">
          <p>{t('form.errorLoadingSlots')}</p>
        </div>
      ) : (
        <form id="current-step-form" onSubmit={handleSubmit} className="date-time-step__form">
          <div className="date-time-step__calendar">
            <div className="date-time-step__calendar-header">
              <button
                type="button"
                className="date-time-step__calendar-nav"
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
              >
                <img src={previousIcon} alt="Previous month" />
              </button>
              <span className="date-time-step__calendar-month">
                {t(`calendar.months.${currentDate.toLocaleString('en-US', { month: 'long' }).toLowerCase()}`)} {currentDate.getFullYear()}
              </span>
              <button
                type="button"
                className="date-time-step__calendar-nav"
                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
              >
                <img src={nextIcon} alt="Next month" />
              </button>
            </div>
            <div className="date-time-step__calendar-weekdays">
              <span>{t('calendar.weekdays.monday')}</span>
              <span>{t('calendar.weekdays.tuesday')}</span>
              <span>{t('calendar.weekdays.wednesday')}</span>
              <span>{t('calendar.weekdays.thursday')}</span>
              <span>{t('calendar.weekdays.friday')}</span>
              <span>{t('calendar.weekdays.saturday')}</span>
              <span>{t('calendar.weekdays.sunday')}</span>
            </div>
            <div className="date-time-step__calendar-days">
              {/* Empty cells for days before the first of the month */}
              {/* Calculate empty cells for days before the 1st of the month */}
              {Array.from(
                { length: (new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() + 6) % 7 },
                (_, i) => i
              ).map((index) => (
                <button key={`empty-${index}`} type="button" className="date-time-step__calendar-day empty" disabled />
              ))}

              {/* Actual days of the month */}
              {Array.from(
                { length: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate() },
                (_, i) => i + 1
              ).map((day) => {
                // Create a date for this calendar day in UTC
                const calendarDayDate = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), day));
                // Format using our helper to ensure consistency with UTC
                const formattedDate = formatDateToYYYYMMDD(calendarDayDate);
                // Check if this date is in the available dates array
                const isAvailable = availableDates.includes(formattedDate);
                
                // No debugging needed here
                
                return (
                  <button
                    key={day}
                    type="button"
                    className={`date-time-step__calendar-day
                      ${selectedDate === formattedDate ? 'selected' : ''}
                      ${!isAvailable ? 'unavailable' : ''}
                    `}
                    onClick={() => setSelectedDate(formattedDate)}
                    disabled={!isAvailable}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="date-time-step__time-slots">
            
            {isLoading ? (
              <p>{t('form.loadingTimeSlots')}</p>
            ) : selectedDate && timeSlots.length > 0 ? (
              timeSlots.map((slot, index) => (
                <button
                  key={index}
                  type="button"
                  className={`date-time-step__time-slot ${selectedTimeSlot === slot ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedTimeSlot(slot);
                    setSelectedTime(`${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`);
                  }}
                >
                  {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                </button>
              ))
            ) : selectedDate && timeSlots.length === 0 ? (
              <p className="date-time-step__no-slots">{t('form.noTimeSlots')}</p>
            ) : (
              <p className="date-time-step__no-date-selected">{t('form.selectDateFirst')}</p>
            )}
          </div>

        </form>
      )}
    </div>
  );
};

